import React, { useState, useEffect } from 'react';
import {
  List,
  Input,
  Select,
  Button,
  Space,
  Tag,
  Badge,
  Empty,
  Spin,
  message,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { KrybotService } from '../../../services/api/krybotService';
import type { KrybotAgent, AgentType } from '../../../types/krybot';
import { krybotAgentTypeTagColor } from '../../../types/krybot';
import { AgentTypeSelectOptions } from '../AgentTypeSelectOptions';
import styles from './AgentManagement.module.css';

const { Search } = Input;
const { Option } = Select;

interface AgentListPanelProps {
  selectedAgentId: string | null;
  onSelectAgent: (agentId: string | null) => void;
  onCreateNew: () => void;
  onRefresh?: () => void;
}

export const AgentListPanel: React.FC<AgentListPanelProps> = ({
  selectedAgentId,
  onSelectAgent,
  onCreateNew,
  onRefresh,
}) => {
  const [agents, setAgents] = useState<KrybotAgent[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [agentTypeFilter, setAgentTypeFilter] = useState<AgentType | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');

  useEffect(() => {
    loadAgents();
  }, [agentTypeFilter, statusFilter]);

  const loadAgents = async () => {
    try {
      setLoading(true);
      const params: { is_active?: boolean; agent_type?: AgentType } = {};
      if (statusFilter !== 'all') {
        params.is_active = statusFilter === 'active';
      }
      if (agentTypeFilter !== 'all') {
        params.agent_type = agentTypeFilter;
      }

      const data = await KrybotService.listAgents(params);
      setAgents(data);
      onRefresh?.();
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to load agents';
      message.error(`Failed to load agents: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const filteredAgents = agents.filter((agent) => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        agent.name.toLowerCase().includes(searchLower) ||
        agent.display_name?.toLowerCase().includes(searchLower) ||
        agent.description?.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  return (
    <div className="agent-list-panel">
      <div className="agent-list-panel-header">
        <Space direction="vertical" style={{ width: '100%' }} size="small">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              console.log('Create New Agent button clicked in AgentListPanel');
              onCreateNew();
            }}
            block
          >
            Create New Agent
          </Button>
          <Search
            placeholder="Search agents..."
            allowClear
            onSearch={setSearchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            prefix={<SearchOutlined />}
          />
          <Space style={{ width: '100%' }}>
            <Select
              value={agentTypeFilter}
              onChange={setAgentTypeFilter}
              style={{ flex: 1 }}
              size="small"
              showSearch
              optionFilterProp="children"
            >
              <Option value="all">All Types</Option>
              <AgentTypeSelectOptions />
            </Select>
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ flex: 1 }}
              size="small"
            >
              <Option value="all">All</Option>
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
            </Select>
          </Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={loadAgents}
            size="small"
            block
          >
            Refresh
          </Button>
        </Space>
      </div>

      <div className="agent-list-panel-content">
        {loading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin />
          </div>
        ) : filteredAgents.length === 0 ? (
          <Empty
            description={searchTerm ? 'No agents found' : 'No agents'}
            style={{ marginTop: '40px' }}
          />
        ) : (
          <List
            dataSource={filteredAgents}
            renderItem={(agent) => (
              <List.Item
                className={`agent-list-item ${selectedAgentId === agent.id ? 'selected' : ''}`}
                onClick={() => onSelectAgent(agent.id)}
                style={{
                  cursor: 'pointer',
                  padding: '12px 16px',
                  borderLeft: selectedAgentId === agent.id ? '3px solid #1890ff' : '3px solid transparent',
                }}
              >
                <List.Item.Meta
                  title={
                    <Space>
                      <span>{agent.display_name || agent.name}</span>
                      <Badge
                        status={agent.is_active ? 'success' : 'default'}
                        text={agent.is_active ? 'Active' : 'Inactive'}
                      />
                    </Space>
                  }
                  description={
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {agent.description || 'No description'}
                      </div>
                      <Space size="small">
                        {agent.agent_type && (
                          <Tag color={krybotAgentTypeTagColor(agent.agent_type)}>
                            {agent.agent_type.toUpperCase()}
                          </Tag>
                        )}
                      </Space>
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </div>
    </div>
  );
};

